import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { AccountInterface } from '@/interfaces/account';
import { formatNumberToCurrency } from '@/utils/currency';
import { capitalizeFirstLetter } from '@/utils/string';
import { mobileWidth } from '@/constants';
import DashboardAccountsHeader from './dashboardAccountsHeader';
import AddAccountModal from '@/modules/portfolio/addAccountModal';
import { useLocalization } from '@/hooks/useLocalization';
import { useCanAddAccounts } from '@/hooks/useCanAddAccounts';
import { useModuleAccess } from '@/permissions';

import { AccordionStyled } from '@/components/common/accordionStyled';
import { useGetAccountsQuery } from '@/store/accountsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { AccountBase } from '@/models/AccountViewModel';
import AccountDetailsFlyout from '@/components/flyouts/AccountDetailsFlyout';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { useSelector } from 'react-redux';
import { WARootState } from '@/store/store';
import {
  selectExcludedAccounts,
  selectExcludedCategories,
  selectExcludedAccountTypes,
  selectExcludedInstitutions,
} from '@/store/filterSlice';
import { useMemo } from 'react';
import { filterAccounts } from '@/utils/accountsToBalanceData';
import { formatAppDate } from '@/utils/date';
import { SyncErrorIcon } from '@/components/connectionError/SyncErrorIcon';
import { useAccountConnectionError } from '@/hooks/useAccountConnectionError';
import { BalanceSheetLegacyErrorIcon } from './balanceSheetLegacyErrorIcon';
import { ContentContainer, WIDGET_PADDING } from '../contentContainer';
import { groupAccountsByType } from './balanceSheetWidgetGroups';
import type { AccountsGroupedByType } from './balanceSheetWidgetGroups';

type BalanceSheetWidgetInterface = StandardWidgetProps;

const BalanceSheetSyncIcon: React.FC<{ institutionId?: number }> = ({
  institutionId,
}) => {
  const connectionError = useAccountConnectionError(institutionId);
  if (!connectionError) {
    return null;
  }
  return <SyncErrorIcon />;
};

export default function BalanceSheetWidget({
  onDataLoaded,
  title,
  container = 'widget',
}: BalanceSheetWidgetInterface) {
  const isMobileMatch = useMediaQuery(mobileWidth);
  const [selectedAccount, setSelectedAccount] = useState<AccountBase | null>(
    null,
  );
  const { t } = useLocalization();
  const widgetTitle = title ?? t('balanceSheet');
  const [dialogOpen, setDialogOpen] = useState(false);
  const authLoaded = useAuthIsLoaded();
  const { validateByCode } = useModuleAccess();
  const canUseAggregation = validateByCode('CanUseAggregation');
  const canAddAccounts = useCanAddAccounts();

  // Get user ID and role for filter persistence
  const userId = useSelector(
    (state: WARootState) => state.appView.UserInfo?.UserId,
  );
  const currentRole = useSelector(
    (state: WARootState) => state.auth.currentRole,
  );

  // Get persisted filter values
  const excludedAccounts = useSelector((state: WARootState) =>
    selectExcludedAccounts(state, userId, currentRole),
  );
  const excludedCategories = useSelector((state: WARootState) =>
    selectExcludedCategories(state, userId, currentRole),
  );
  const excludedAccountTypes = useSelector((state: WARootState) =>
    selectExcludedAccountTypes(state, userId, currentRole),
  );
  const excludedInstitutions = useSelector((state: WARootState) =>
    selectExcludedInstitutions(state, userId, currentRole),
  );

  const {
    data: accountsData,
    isFetching,
    isUninitialized,
  } = useGetAccountsQuery(
    authLoaded
      ? {
          showClosed: false,
        }
      : skipToken,
  );
  const isLoading: boolean = !authLoaded || isFetching || isUninitialized;

  // Filter accounts based on excluded filters
  const filteredAccounts = useMemo<AccountBase[]>(() => {
    const records: AccountBase[] | undefined = accountsData?.Records;
    if (!records) return [];

    const hasFilters =
      excludedAccounts.length > 0 ||
      excludedCategories.length > 0 ||
      excludedAccountTypes.length > 0 ||
      excludedInstitutions.length > 0;

    if (!hasFilters) {
      return records;
    }

    return filterAccounts(records, {
      excludedAccounts:
        excludedAccounts.length > 0 ? excludedAccounts : undefined,
      excludedCategories:
        excludedCategories.length > 0 ? excludedCategories : undefined,
      excludedAccountTypes:
        excludedAccountTypes.length > 0 ? excludedAccountTypes : undefined,
      excludedInstitutions:
        excludedInstitutions.length > 0 ? excludedInstitutions : undefined,
    });
  }, [
    accountsData?.Records,
    excludedAccounts,
    excludedCategories,
    excludedAccountTypes,
    excludedInstitutions,
  ]);

  const assets = filteredAccounts
    .filter((account: AccountBase) => account.IsAsset)
    .reduce((acc: number, curr: AccountBase) => acc + curr.Balance, 0);
  const liabilities = filteredAccounts
    .filter((account: AccountBase) => !account.IsAsset)
    .reduce((acc: number, curr: AccountBase) => acc + curr.Balance, 0);

  useEffect(() => {
    if (onDataLoaded && !isLoading) {
      setTimeout(() => {
        onDataLoaded();
      }, 2000);
    }
  }, [isLoading, onDataLoaded]);

  const handleAccountClick = (account: AccountInterface) => {
    const accountToSelect = filteredAccounts.find(
      (acc: AccountBase) => acc.AccountId === account.id,
    );
    if (accountToSelect) {
      setSelectedAccount(accountToSelect);
    }
  };

  const accountTypes = groupAccountsByType(filteredAccounts);

  const getAccountSum = (accountsToSum: Array<AccountInterface>) => {
    const total = accountsToSum.reduce((accumulator: number, currentValue) => {
      let value = 0;
      if (currentValue.balance) {
        value = currentValue.balance;
      }

      if (currentValue.value) {
        value = currentValue.value;
      }

      return accumulator + value;
    }, 0);
    return formatNumberToCurrency(total, accountsToSum[0].currency);
  };

  const renderAccounts = (accountsToRender: AccountsGroupedByType[]) => {
    // Hide categories with zero accounts
    const filteredAccountsToRender = accountsToRender.filter(
      (acctType) => acctType.accounts.length > 0,
    );

    if (isMobileMatch) {
      return (
        <>
          {filteredAccountsToRender.map((accountType) => (
            <AccordionStyled
              key={`${String(accountType.classificationId)}-${accountType.isAsset ? 'asset' : 'liability'}`}
              disableGutters
              elevation={4}
              sx={{ mb: 1, mx: 2 }}
            >
              {renderAccountsSummary(accountType)}
              {accountType.accounts.length > 0 ? (
                renderAccountsDetails(accountType)
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                  >
                    {t('noAccountsOfThisType')}
                  </Typography>
                </Box>
              )}
            </AccordionStyled>
          ))}
        </>
      );
    }

    return (
      <>
        {filteredAccountsToRender.map((accountType) => (
          <Accordion
            key={`${String(accountType.classificationId)}-${accountType.isAsset ? 'asset' : 'liability'}`}
            disableGutters
            // defaultExpanded={totalAccounts && totalAccounts < 21}
            elevation={0}
            sx={{
              '&:before': {
                display: 'none',
              },
            }}
          >
            {renderAccountsSummary(accountType)}
            {accountType.accounts.length > 0 ? (
              renderAccountsDetails(accountType)
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography
                  variant='body2'
                  color='text.secondary'
                >
                  {t('noAccountsOfThisType')}
                </Typography>
              </Box>
            )}
          </Accordion>
        ))}
      </>
    );
  };

  const renderAccountsSummary = (accountType: AccountsGroupedByType) => (
    <AccordionSummary
      className='gradient-row'
      expandIcon={<ExpandMoreIcon />}
      sx={{
        flexDirection: 'row-reverse',
        gap: 1,
        '.MuiAccordionSummary-content': {
          justifyContent: 'space-between',
        },
      }}
    >
      <Typography variant='subtitle1'>
        {accountType.type
          .split(' ')
          .map((word) => capitalizeFirstLetter(word))
          .join(' ')}
      </Typography>
      <Typography variant='subtitle1'>
        {isLoading ? (
          <Skeleton width={60} />
        ) : accountType.accounts.length > 0 ? (
          getAccountSum(accountType.accounts)
        ) : (
          '$0.00'
        )}
      </Typography>
    </AccordionSummary>
  );

  const renderAccountsDetails = (accountType: AccountsGroupedByType) => (
    <AccordionDetails
      sx={{
        display: 'flex',
        flexDirection: 'column',
        p: isMobileMatch ? 2 : 0,
        gap: isMobileMatch ? 1 : 0,
      }}
    >
      {accountType.accounts.map((account, index) => (
        <React.Fragment key={`${String(account.id)}-${String(index)}`}>
          <Box
            onClick={() => {
              handleAccountClick(account);
            }}
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              p: isMobileMatch ? 1 : 2,
              pr: isMobileMatch ? 1 : 3,
              pl: isMobileMatch ? 1 : 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant='subtitle2'
                sx={{ wordBreak: 'break-word' }}
              >
                {account.name}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
              >
                {account.institution}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              <Typography variant='subtitle2'>
                {formatNumberToCurrency(
                  account.balance ?? account.value,
                  account.currency,
                )}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BalanceSheetLegacyErrorIcon hasError={account.hasError} />
                {account.lastUpdated && (
                  <Stack
                    direction='row'
                    alignItems='center'
                    gap={0.5}
                  >
                    <Typography
                      variant='caption'
                      color='text.secondary'
                    >
                      {formatAppDate(account.lastUpdated, {
                        fallback: '',
                        format: 'MM/DD/YYYY',
                        locale: 'en',
                      })}
                    </Typography>
                    <BalanceSheetSyncIcon
                      institutionId={account.institutionId}
                    />
                  </Stack>
                )}
              </Box>
            </Box>
          </Box>
          {index < accountType.accounts.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </AccordionDetails>
  );

  return (
    <ContentContainer
      sx={{ width: isMobileMatch ? '100%' : 400, height: 'fit-content' }}
      container={container}
      hasCustomPadding
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        sx={{ p: WIDGET_PADDING }}
      >
        <Typography variant='h5'>{widgetTitle}</Typography>
        {canUseAggregation && (
          <Box>
            <Button
              size='small'
              variant='outlined'
              disabled={!canAddAccounts}
              onClick={() => {
                if (!canAddAccounts) {
                  return;
                }
                setDialogOpen(true);
              }}
              startIcon={<AddOutlinedIcon />}
            >
              {t('addAccount')}
            </Button>
            {dialogOpen && (
              <AddAccountModal
                open={dialogOpen}
                onClose={() => {
                  setDialogOpen(false);
                }}
              />
            )}
          </Box>
        )}
      </Box>
      <DashboardAccountsHeader
        currency={'USD'}
        assets={assets}
        liabilities={liabilities}
        isLoading={isLoading}
      />
      <Divider sx={{ mt: 1, mb: 1 }} />
      {!isLoading ? (
        <>
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded
            sx={{
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ArrowDropDownIcon />}
              sx={{ display: 'inline-flex;' }}
            >
              <Typography variant='subtitle2'>{t('assets')}</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                display: 'flex',
                p: 0,
                m: 0,
                flexDirection: 'column',
              }}
            >
              {renderAccounts(
                accountTypes.filter((at: AccountsGroupedByType) => at.isAsset),
              )}
            </AccordionDetails>
          </Accordion>
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded
            sx={{
              mb: 1,
              '&:before': {
                display: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ArrowDropDownIcon />}
              sx={{ display: 'inline-flex;' }}
            >
              <Typography variant='subtitle2'>{t('liabilities')}</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                display: 'flex',
                p: 0,
                m: 0,
                flexDirection: 'column',
              }}
            >
              {renderAccounts(
                accountTypes.filter((at: AccountsGroupedByType) => !at.isAsset),
              )}
            </AccordionDetails>
          </Accordion>
        </>
      ) : (
        <LoadingSpinner />
      )}
      <AccountDetailsFlyout
        open={!!selectedAccount}
        toggleDrawer={() => {
          setSelectedAccount(null);
        }}
        account={selectedAccount}
      />
    </ContentContainer>
  );
}
